import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/utils/api';
import { 
  ImageIcon, 
  Sparkles, 
  Download, 
  Share2, 
  Copy, 
  Check,
  RefreshCw,
  Building2,
  TrendingUp,
  ChevronDown,
  Loader2,
  Eye,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatINR } from '@/utils/currency';
import BrokerSidebar from '@/components/broker/BrokerSidebar';

// Funding stage colors for preview
const stageStyles = {
  'JUST OPENED': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/50' },
  'EARLY BIRD': { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/50' },
  'GAINING TRACTION': { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/50' },
  'HOT PROPERTY': { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/50' },
  'ALMOST FULL': { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/50' },
  'FULLY FUNDED': { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/50' },
};

export default function BrokerEDMGenerator() {
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [renderingImage, setRenderingImage] = useState(false);
  const [generatedEDM, setGeneratedEDM] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [copied, setCopied] = useState(false);
  const [brokerData, setBrokerData] = useState(null);
  const [fundingStages, setFundingStages] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.id || user.role !== 'broker') {
      navigate('/broker/auth');
      return;
    }
    
    fetchData(user.id);
    fetchFundingStages();
  }, [navigate]);

  const fetchData = async (userId) => {
    try {
      setLoading(true);
      
      // Fetch available units
      const unitsRes = await api.get('/projects/units/all');
      const openUnits = (unitsRes.data || []).filter(u => 
        u.lifecycleState === 'OPEN' || u.status === 'active' || u.status === 'open'
      );
      setUnits(openUnits);
      
      // Fetch broker data
      const brokerRes = await api.get(`/broker/profile/${userId}`);
      setBrokerData(brokerRes.data);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFundingStages = async () => {
    try {
      const res = await api.get('/edm/stages');
      setFundingStages(res.data.stages || []);
    } catch (error) {
      console.error('Error fetching stages:', error);
    }
  };

  const handleGenerateEDM = async () => {
    if (!selectedUnit) {
      toast.error('Please select a property first');
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    try {
      setGenerating(true);
      setGeneratedEDM(null);
      setGeneratedImage(null);
      
      const res = await api.post('/edm/generate', {
        property_id: selectedUnit.projectId,
        unit_id: selectedUnit.unitId,
        broker_id: user.id
      });
      
      setGeneratedEDM(res.data);
      toast.success('EDM generated with AI tagline!');
      
    } catch (error) {
      console.error('Error generating EDM:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate EDM');
    } finally {
      setGenerating(false);
    }
  };

  const handleRenderImage = async () => {
    if (!generatedEDM?.html) {
      toast.error('Generate EDM first');
      return;
    }

    try {
      setRenderingImage(true);
      
      const res = await api.post('/edm/render-image', { html: generatedEDM.html });
      
      setGeneratedImage(res.data.image);
      toast.success('Image rendered successfully!');
      
    } catch (error) {
      console.error('Error rendering image:', error);
      toast.error('Failed to render image');
    } finally {
      setRenderingImage(false);
    }
  };

  const handleDownloadImage = () => {
    if (!generatedImage) return;
    
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `EDM_${selectedUnit?.unitName || 'property'}_${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Image downloaded!');
  };

  const handleShareWhatsApp = async () => {
    if (!generatedEDM?.edm_id) return;
    
    try {
      // Track share
      await api.post(`/edm/track-share/${generatedEDM.edm_id}?platform=whatsapp`);
      
      // Format location - handle both string and object formats
      const locationStr = typeof generatedEDM.data.location === 'object' 
        ? `${generatedEDM.data.location.sector || ''}, ${generatedEDM.data.location.city || ''}`
        : generatedEDM.data.location;
      
      const text = encodeURIComponent(
        `🏢 Check out this investment opportunity!\n\n` +
        `${generatedEDM.data.property_name}\n` +
        `📍 ${locationStr}\n\n` +
        `💰 ${generatedEDM.data.funded_amount} / ${generatedEDM.data.target_amount} funded\n\n` +
        `"${generatedEDM.data.tagline}"\n\n` +
        `Contact: ${brokerData?.fullName || 'Your Broker'}`
      );
      
      window.open(`https://wa.me/?text=${text}`, '_blank');
      toast.success('Opening WhatsApp...');
      
    } catch (error) {
      console.error('Share error:', error);
    }
  };

  const handleCopyTagline = () => {
    if (!generatedEDM?.data?.tagline) return;
    
    navigator.clipboard.writeText(generatedEDM.data.tagline);
    setCopied(true);
    toast.success('Tagline copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const getStageStyle = (stageName) => {
    return stageStyles[stageName] || stageStyles['GAINING TRACTION'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white flex">
      <BrokerSidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-[#4ADE80]/20 to-[#22C55E]/20 rounded-lg border border-[#4ADE80]/30">
              <Sparkles className="w-6 h-6 text-[#4ADE80]" />
            </div>
            <h1 className="text-2xl md:text-3xl font-light tracking-tight">Smart EDM Generator</h1>
          </div>
          <p className="text-white/50 text-sm md:text-base">
            Generate AI-powered marketing materials with dynamic content based on funding status
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Left Column - Controls */}
          <div className="space-y-6">
            {/* Property Selector */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6"
            >
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#4ADE80]" />
                Select Property
              </h2>
              
              <div className="relative">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="w-full flex items-center justify-between p-4 bg-[#111] border border-white/10 rounded-lg hover:border-[#4ADE80]/50 transition-colors"
                  data-testid="property-selector"
                >
                  <span className={selectedUnit ? 'text-white' : 'text-white/50'}>
                    {selectedUnit ? selectedUnit.unitName : 'Choose a property...'}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-white/50 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {showDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute z-50 w-full mt-2 bg-[#111] border border-white/10 rounded-lg shadow-xl max-h-64 overflow-y-auto"
                    >
                      {units.length === 0 ? (
                        <div className="p-4 text-white/50 text-center">No properties available</div>
                      ) : (
                        units.map((unit) => (
                          <button
                            key={unit.unitId}
                            onClick={() => {
                              setSelectedUnit(unit);
                              setShowDropdown(false);
                              setGeneratedEDM(null);
                              setGeneratedImage(null);
                            }}
                            className={`w-full p-4 text-left hover:bg-white/5 transition-colors border-b border-white/5 last:border-0 ${
                              selectedUnit?.unitId === unit.unitId ? 'bg-[#4ADE80]/10' : ''
                            }`}
                          >
                            <div className="font-medium">{unit.unitName}</div>
                            <div className="text-sm text-white/50 mt-1">
                              {formatINR(unit.currentFunding || 0)} / {formatINR(unit.targetFunding || unit.price || 0)} funded
                            </div>
                          </button>
                        ))
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {selectedUnit && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-4 p-4 bg-[#111] rounded-lg border border-white/5"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white/50 text-sm">Funding Progress</span>
                    <span className="text-[#4ADE80] font-medium">
                      {((selectedUnit.currentFunding || 0) / (selectedUnit.targetFunding || selectedUnit.price || 1) * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${((selectedUnit.currentFunding || 0) / (selectedUnit.targetFunding || selectedUnit.price || 1) * 100)}%` }}
                      className="h-full bg-gradient-to-r from-[#4ADE80] to-[#22C55E] rounded-full"
                    />
                  </div>
                </motion.div>
              )}
            </motion.div>

            {/* Generate Button */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              onClick={handleGenerateEDM}
              disabled={!selectedUnit || generating}
              className={`w-full p-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
                selectedUnit && !generating
                  ? 'bg-gradient-to-r from-[#4ADE80] to-[#22C55E] text-black hover:shadow-lg hover:shadow-[#4ADE80]/20'
                  : 'bg-white/10 text-white/30 cursor-not-allowed'
              }`}
              data-testid="generate-edm-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating with AI...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generate Smart EDM
                </>
              )}
            </motion.button>

            {/* Funding Stages Info */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6"
            >
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#4ADE80]" />
                Dynamic Funding Stages
              </h2>
              <p className="text-white/50 text-sm mb-4">
                EDM content and design automatically adapts based on funding progress
              </p>
              
              <div className="space-y-2">
                {fundingStages.map((stage) => {
                  const style = getStageStyle(stage.badge?.replace(/[^A-Z\s]/g, '').trim() || stage.name);
                  return (
                    <div 
                      key={stage.name}
                      className={`p-3 rounded-lg ${style.bg} border ${style.border} flex items-center justify-between`}
                    >
                      <div className="flex items-center gap-2">
                        <span>{stage.emoji}</span>
                        <span className={`text-sm font-medium ${style.text}`}>{stage.badge}</span>
                      </div>
                      <span className="text-xs text-white/40">
                        {stage.range[0]}% - {stage.range[1]}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>

          {/* Right Column - Preview & Actions */}
          <div className="space-y-6">
            {/* EDM Preview */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6"
            >
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#4ADE80]" />
                EDM Preview
              </h2>
              
              {!generatedEDM ? (
                <div className="aspect-[9/16] max-h-[500px] bg-[#111] rounded-lg border border-dashed border-white/10 flex flex-col items-center justify-center text-white/30">
                  <ImageIcon className="w-12 h-12 mb-3" />
                  <p className="text-sm">Select a property and generate</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Stage Badge */}
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStageStyle(generatedEDM.stage?.name).bg} ${getStageStyle(generatedEDM.stage?.name).text}`}>
                      {generatedEDM.stage?.emoji} {generatedEDM.stage?.name}
                    </span>
                  </div>

                  {/* Data Preview */}
                  <div className="bg-[#111] rounded-lg p-4 space-y-3">
                    <h3 className="font-semibold text-lg">{generatedEDM.data?.property_name}</h3>
                    <p className="text-white/50 text-sm">📍 {
                      typeof generatedEDM.data?.location === 'object' 
                        ? `${generatedEDM.data.location.sector || ''}, ${generatedEDM.data.location.city || ''}`
                        : generatedEDM.data?.location
                    }</p>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#4ADE80] font-medium">{generatedEDM.data?.funded_amount}</span>
                      <span className="text-white/30">/</span>
                      <span className="text-white/50">{generatedEDM.data?.target_amount}</span>
                    </div>
                    
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#4ADE80] to-[#22C55E] rounded-full"
                        style={{ width: `${generatedEDM.data?.funded_percent || 0}%` }}
                      />
                    </div>
                  </div>

                  {/* AI Tagline */}
                  <div className="bg-gradient-to-r from-[#4ADE80]/10 to-[#22C55E]/10 border border-[#4ADE80]/20 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-[#4ADE80] font-medium flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        AI-Generated Tagline
                      </span>
                      <button
                        onClick={handleCopyTagline}
                        className="text-white/50 hover:text-white transition-colors"
                      >
                        {copied ? <Check className="w-4 h-4 text-[#4ADE80]" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-white font-medium italic">"{generatedEDM.data?.tagline}"</p>
                  </div>

                  {/* Broker Info */}
                  <div className="flex items-center gap-3 p-3 bg-[#111] rounded-lg">
                    <div className="w-10 h-10 bg-[#4ADE80] rounded-lg flex items-center justify-center text-black font-bold">
                      {generatedEDM.data?.broker_name?.charAt(0) || 'B'}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{generatedEDM.data?.broker_name}</div>
                      <div className="text-xs text-white/50">Your personalized EDM</div>
                    </div>
                  </div>

                  {/* Rendered Image */}
                  {generatedImage && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-white/70 mb-2">Rendered Image</h4>
                      <img 
                        src={generatedImage} 
                        alt="Generated EDM" 
                        className="w-full rounded-lg border border-white/10"
                      />
                    </div>
                  )}
                </div>
              )}
            </motion.div>

            {/* Action Buttons */}
            {generatedEDM && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-2 gap-3"
              >
                <button
                  onClick={handleRenderImage}
                  disabled={renderingImage}
                  className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg hover:border-[#4ADE80]/50 transition-colors flex items-center justify-center gap-2"
                  data-testid="render-image-btn"
                >
                  {renderingImage ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ImageIcon className="w-4 h-4" />
                  )}
                  <span className="text-sm">Render Image</span>
                </button>
                
                <button
                  onClick={handleGenerateEDM}
                  disabled={generating}
                  className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg hover:border-[#4ADE80]/50 transition-colors flex items-center justify-center gap-2"
                  data-testid="regenerate-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
                  <span className="text-sm">Regenerate</span>
                </button>
                
                {generatedImage && (
                  <>
                    <button
                      onClick={handleDownloadImage}
                      className="p-3 bg-[#0A0A0A] border border-white/10 rounded-lg hover:border-[#4ADE80]/50 transition-colors flex items-center justify-center gap-2"
                      data-testid="download-btn"
                    >
                      <Download className="w-4 h-4" />
                      <span className="text-sm">Download</span>
                    </button>
                    
                    <button
                      onClick={handleShareWhatsApp}
                      className="p-3 bg-[#25D366]/20 border border-[#25D366]/30 rounded-lg hover:bg-[#25D366]/30 transition-colors flex items-center justify-center gap-2 text-[#25D366]"
                      data-testid="share-whatsapp-btn"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-sm">WhatsApp</span>
                    </button>
                  </>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
